/**
 * Presentation Layer - CsvFileDropZone Component
 * CSVファイルのドラッグ&ドロップ領域コンポーネント
 */

import React, { useCallback, useState, useRef } from 'react';
import { Typography, Paper } from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';

/**
 * Props定義
 */
export interface CsvFileDropZoneProps {
  /** ファイル選択時のコールバック */
  onFileSelect: (file: File) => void;
  /** 無効化フラグ */
  isDisabled?: boolean;
  /** ローディング中フラグ */
  isLoading?: boolean;
}

/**
 * CSVファイルドロップゾーンコンポーネント
 */
export const CsvFileDropZone: React.FC<CsvFileDropZoneProps> = ({
  onFileSelect,
  isDisabled = false,
  isLoading = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * ドラッグエンター
   */
  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (isDisabled || isLoading) return;

      setDragCounter((prev) => {
        const newCounter = prev + 1;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
          setIsDragOver(true);
        }
        return newCounter;
      });
    },
    [isDisabled, isLoading]
  );

  /**
   * ドラッグリーブ
   */
  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (isDisabled || isLoading) return;

      setDragCounter((prev) => {
        const newCounter = prev - 1;
        if (newCounter <= 0) {
          setIsDragOver(false);
          return 0;
        }
        return newCounter;
      });
    },
    [isDisabled, isLoading]
  );

  /**
   * ドラッグオーバー
   */
  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
    },
    []
  );

  /**
   * ドロップ
   */
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDragCounter(0);

      if (isDisabled || isLoading) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        onFileSelect(files[0]);
      }
    },
    [isDisabled, isLoading, onFileSelect]
  );

  /**
   * ファイル選択ダイアログを開く
   */
  const handleClick = useCallback(() => {
    if (isDisabled || isLoading) return;
    fileInputRef.current?.click();
  }, [isDisabled, isLoading]);

  /**
   * ファイル選択時
   */
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        onFileSelect(files[0]);
      }
      // 同じファイルを再選択できるようにリセット
      e.target.value = '';
    },
    [onFileSelect]
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        textAlign: 'center',
        cursor: isDisabled || isLoading ? 'not-allowed' : 'pointer',
        backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
        borderStyle: 'dashed',
        borderWidth: 2,
        borderColor: isDragOver ? 'primary.main' : 'divider',
        transition: 'all 0.3s ease',
      }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        id="csv-file-input"
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={isDisabled || isLoading}
      />
      <CloudUploadIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        ここにCSVファイルをドロップ
      </Typography>
      <Typography variant="body2" color="text.secondary">
        または、クリックしてファイルを選択
      </Typography>
    </Paper>
  );
};
